<script setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
  quickstartClosed,
  quickstartCompleted,
  quickstartCtaClicked,
  quickstartHelpClicked,
  quickstartStepExpanded,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import Accordion from "@/components-ng/ui/accordion/Accordion.vue";
import AccordionContent from "@/components-ng/ui/accordion/AccordionContent.vue";
import AccordionItem from "@/components-ng/ui/accordion/AccordionItem.vue";
import AccordionTrigger from "@/components-ng/ui/accordion/AccordionTrigger.vue";
import ProgressBar from "@/components-ng/ui/progress/ProgressBar.vue";
import { useLayout } from "@/composables/useLayout";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import { ArtHandler } from "@/handlers/artHandler";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { TeamHandler } from "@/handlers/teamHandler";
import { UserHandler } from "@/handlers/userHandler";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";

import BaseButton from "../BaseButton.vue";
import BaseSpinner from "../BaseSpinner.vue";
import InviteUsersDialog from "../InviteUsersDialog.vue";
import SvgIcon from "../SvgIcon.vue";
import { useQuickStart } from "./useQuickStart";

const STORAGE_COMPLETED = "quickstart-completed-tracked";

const router = useRouter();
const store = useAppStore();
const { showFrame } = useLayout();
const { sidebarVisible, setSidebarOpen, canSeeQuickStart } = useQuickStart();

const visible = computed(() => showFrame.value && sidebarVisible.value);

function openSupportChat() {
  trackEvent(quickstartHelpClicked("support"));
  window.Intercom?.("showNewMessage");
}

function handleDocsClick() {
  trackEvent(quickstartHelpClicked("docs"));
}

function handleAccordionUpdate(value) {
  if (value) trackEvent(quickstartStepExpanded(value));
}

const items = [
  {
    id: "teams",
    title: "Add your teams",
    description: "The smallest group that plans and ships together.",
    bullets: [
      "Each team gets its own board",
      "Track capacity and commitments",
      "Plan toward shared objectives",
    ],
    ctaLabel: "Create team",
    to: { name: "createTeam" },
  },
  {
    id: "art",
    title: "Group teams into ARTs",
    description: "ARTs (Agile Release Trains) bundle the teams that plan together.",
    bullets: [
      "Plan in lockstep across teams",
      "Surface cross-team dependencies",
      "One planning event per train",
    ],
    ctaLabel: "Create ART",
    to: { name: "createArt" },
  },
  {
    id: "alm",
    title: "Connect your ALM tool",
    description: "Sync with Jira, Azure DevOps, or Rally.",
    bullets: [
      "Issues from your ALM tool appear on team boards",
      "Skip copy-paste between tools",
      "Changes sync back in real time",
    ],
    ctaLabel: "Connect ALM",
    to: { name: "almConnections" },
  },
  {
    id: "session",
    title: "Create a PI Session",
    description: "The live event where everyone plans the next PI together.",
    bullets: [
      "All teams plan side by side, live",
      "Capture risks and objectives in flight",
      "Reuse setup for the next PI",
    ],
    ctaLabel: "Create session",
    to: { name: "addnewsafepiplanning" },
  },
  {
    id: "invite",
    title: "Invite your team",
    description: "PI Planning is a team sport — bring in the people you'll plan with.",
    bullets: [
      "Everyone gets an email with a join link",
      "Invitees land on their team, ready to plan",
      "Adjust roles later on the Users page",
    ],
    ctaLabel: "Invite users",
  },
];

const progress = ref(null);
async function loadProgress() {
  const context = createHandlerContext();
  const [teamsR, artsR, sessR, almR, usersR] = await Promise.allSettled([
    new TeamHandler().getTeams(),
    new ArtHandler().getArts(),
    new SessionsHandler(context).getSessions(),
    new AlmConfigurationHandler(context).getAlmConnections(),
    new UserHandler().getUsers(),
  ]);
  const wrapped = (r) => r.status === "fulfilled" && (r.value?.data || []).length > 0;
  const raw = (r) => r.status === "fulfilled" && (r.value || []).length > 0;
  const usersCount =
    usersR.status === "fulfilled"
      ? (usersR.value.pagination?.total ?? usersR.value.data?.length ?? 0)
      : 0;
  progress.value = {
    teams: wrapped(teamsR),
    art: wrapped(artsR),
    session: raw(sessR),
    alm: raw(almR),
    invite: usersCount > 1,
  };

  const everyDone = items.every((i) => progress.value[i.id]);
  if (everyDone && localStorage.getItem(STORAGE_COMPLETED) !== "true") {
    localStorage.setItem(STORAGE_COMPLETED, "true");
    trackEvent(quickstartCompleted());
  }
}

watch(
  canSeeQuickStart,
  (can) => {
    if (can && !progress.value) loadProgress();
  },
  { immediate: true },
);

const completedCount = computed(() => items.filter((i) => progress.value?.[i.id]).length);
const totalCount = items.length;
const progressPercent = computed(() => (completedCount.value / totalCount) * 100);
const allComplete = computed(() => completedCount.value === totalCount);

const defaultExpandedId = computed(() => items.find((i) => !progress.value?.[i.id])?.id ?? null);

const inviteOpen = ref(false);

function handleCta(item) {
  trackEvent(quickstartCtaClicked(item.id));
  if (item.id === "invite") {
    inviteOpen.value = true;
    return;
  }
  router.push(item.to);
}

function onInvited() {
  if (progress.value) progress.value = { ...progress.value, invite: true };
}

function close() {
  setSidebarOpen(false);
  trackEvent(quickstartClosed());
}
</script>

<template>
  <aside v-if="visible" class="quick-start-sidebar" aria-labelledby="quickstart-title">
    <BaseButton
      class="close"
      variant="ghost"
      size="small"
      icon-before="system/close"
      aria-label="Close Quickstart"
      @click="close"
    />
    <div v-if="!progress" class="loading">
      <BaseSpinner delayed />
    </div>
    <template v-else>
      <header class="header">
        <h2 id="quickstart-title" class="header-title">Quickstart</h2>
        <p class="header-subtitle">
          {{ allComplete ? "You're all set." : `${totalCount} steps to your first PI Session.` }}
        </p>
      </header>

      <div class="progress-wrap">
        <ProgressBar
          :model-value="progressPercent"
          :get-value-label="() => `${completedCount} of ${totalCount} steps complete`"
        />
        <span class="progress-label">{{ completedCount }}/{{ totalCount }}</span>
      </div>

      <div class="items">
        <Accordion
          type="single"
          collapsible
          :default-value="defaultExpandedId"
          @update:model-value="handleAccordionUpdate"
        >
          <AccordionItem
            v-for="item in items"
            :key="item.id"
            :value="item.id"
            :class="['item', { 'is-complete': progress[item.id] }]"
          >
            <AccordionTrigger>
              <SvgIcon
                :name="progress[item.id] ? 'system/checkmark-circled-filled' : 'system/circle'"
                width="18"
                height="18"
                class="status"
              />
              <span class="title">{{ item.title }}</span>
            </AccordionTrigger>
            <AccordionContent>
              <div class="body">
                <p class="description">{{ item.description }}</p>
                <ul v-if="item.bullets?.length" class="bullets">
                  <li v-for="b in item.bullets" :key="b">{{ b }}</li>
                </ul>
                <BaseButton variant="filled" color="primary" size="small" @click="handleCta(item)">
                  {{ item.ctaLabel }}
                </BaseButton>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div class="help">
        <p class="help-text">Need help getting set up?</p>
        <div class="help-links">
          <a
            class="help-link"
            href="https://help.piplanning.io/en/"
            target="_blank"
            rel="noopener"
            @click="handleDocsClick"
          >
            View docs
          </a>
          <template v-if="store.intercomAvailable">
            <span class="help-sep" aria-hidden="true">·</span>
            <button class="help-link" type="button" @click="openSupportChat">
              Contact support
            </button>
          </template>
        </div>
      </div>
    </template>

    <InviteUsersDialog v-model:open="inviteOpen" source="quickstart" @invited="onInvited" />
  </aside>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

.quick-start-sidebar {
  position: sticky;
  top: 0;
  align-self: start;
  display: flex;
  flex-direction: column;
  width: var(--quickstart-sidebar-width);
  height: 100vh;
  border-left: 1px solid c.$border-subtle;
  background-color: c.$background-ui;
}

.header {
  padding: 1.5rem 1.75rem 0.5rem;
}

.header-title {
  @include typography.large("bold");

  margin: 0;
  color: c.$text;
}

.header-subtitle {
  @include typography.small;

  margin: 0.25rem 0 0;
  color: c.$text-subtle;
}

.close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
}

.loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.progress-wrap {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0 1.75rem 1rem;
}

.progress-label {
  @include typography.small;

  color: c.$text-subtle;
  flex-shrink: 0;
}

.items {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.status {
  flex-shrink: 0;
  color: c.$grey-light;

  .is-complete & {
    color: c.$green;
  }
}

.title {
  @include typography.medium;

  flex: 1;
  color: c.$text;

  .is-complete & {
    color: c.$text-subtle;
  }
}

.body {
  padding: 0.25rem 1.75rem 1rem 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.description {
  @include typography.small;

  color: c.$text-subtle;
  margin: 0;
}

.bullets {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  li {
    @include typography.small;

    position: relative;
    padding-left: 1rem;
    color: c.$text-subtle;
    display: list-item;

    &::before {
      content: "";
      position: absolute;
      left: 0.25rem;
      top: 0.5em;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: c.$grey-light;
    }
  }
}

.help {
  margin-top: 1.5rem;
  padding: 1rem 1.75rem;
  border-top: 1px solid c.$border-subtle;
}

.help-text {
  @include typography.small;

  margin: 0 0 0.25rem;
  color: c.$text;
}

.help-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.help-link {
  @include typography.small;

  appearance: none;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  color: c.$main;
  text-decoration: underline;
  cursor: pointer;
}

.help-sep {
  color: c.$text-subtle;
}
</style>
